import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { collection, addDoc, updateDoc, doc, getDocs, writeBatch, arrayUnion, deleteField } from 'firebase/firestore';
import { db, auth } from '../../../../firebase';
import { CalendarEvent } from '../../job-summary/Interfaces';
import Select, { StylesConfig } from 'react-select';
import moment from 'moment';
import { KTSVG } from '../../../../_metronic/helpers';

interface SelectOption {
  label: string;
  value: string;
}

/**
 * Identifies WHICH occurrence of a recurring series the user opened.
 *
 * A recurring event is one Firestore document; the calendar materialises it into
 * many chips. Without this, every chip resolved back to the base document, so
 * editing the 5th occurrence rewrote the series from its original date and
 * deleting it wiped every occurrence, past and future.
 */
export interface EventOccurrenceContext {
  /** The occurrence's own day, 'YYYY-MM-DD'. This is what gets recorded as an exception. */
  date: string;
  /** The occurrence's own start ('YYYY-MM-DD' when all-day, ISO otherwise). */
  start: string;
  /** The occurrence's own end, if the series has one. */
  end?: string;
}

/** What a save/delete applies to when the user opened one occurrence of a series. */
type EditScope = 'occurrence' | 'series';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The BASE Firestore document, unmodified — NOT the occurrence's dates. The
   * occurrence's dates travel in `occurrence` so that "edit the whole series"
   * can still show and write the series' own start/end.
   */
  event: CalendarEvent | null;
  /** Day the user asked to create on (e.g. the clicked calendar cell). Only used when creating. */
  defaultDate?: Date | null;
  /**
   * Set ONLY when the user opened one occurrence of a recurring series. Must be
   * referentially stable (hold it in state) — it is an effect dependency.
   */
  occurrence?: EventOccurrenceContext | null;
  onNewEventAdded?: (event: CalendarEvent) => void;
  onEventDeleted?: (eventId: string) => void;
}

const NewEventModal: React.FC<EventModalProps> = ({ isOpen, onClose, onNewEventAdded, event, defaultDate, occurrence, onEventDeleted }) => {
  const [customers, setCustomers] = useState<SelectOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Save/delete failures used to be console-only: the spinner cleared and the modal
  // just sat there, which looked identical to a successful save.
  const [error, setError] = useState<string | null>(null);
  // Defaults to the SAFE scope: touching one day can be undone by hand, rewriting
  // or cancelling a whole series cannot.
  const [editScope, setEditScope] = useState<EditScope>('occurrence');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    client: [] as string[],
    start: moment().format('YYYY-MM-DDTHH:mm'),
    end: moment().add(1, 'hours').format('YYYY-MM-DDTHH:mm'),
    allDay: false,
    description: '',
    location: '',
    attendees: '',
    recurrence: '',
    recurrenceEnd: '',
    colour: '#3788d8',
  });

  const colorOptions = [
    '#3788d8', '#5e72e4', '#11cdef', '#2dce89', '#fb6340', 
    '#f5365c', '#ffd600', '#ff8a00', '#8965e0', '#525f7f'
  ];

  // Theme-aware styles for React-Select
  const customSelectStyles: StylesConfig<SelectOption, true> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      backgroundColor: 'var(--bs-body-bg)',
      borderColor: state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)',
      borderRadius: '0.475rem',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25)' : 'none',
      color: 'var(--bs-body-color)',
      '&:hover': {
        borderColor: 'var(--bs-primary)',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--bs-body-bg)',
      border: '1px solid var(--bs-border-color)',
      borderRadius: '0.475rem',
      boxShadow: 'var(--bs-dropdown-box-shadow)',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: 'var(--bs-body-bg)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'var(--bs-primary)' 
        : state.isFocused 
          ? 'var(--bs-component-hover-bg)' 
          : 'transparent',
      color: state.isSelected 
        ? 'var(--bs-white)' 
        : 'var(--bs-body-color)',
      '&:hover': {
        backgroundColor: state.isSelected 
          ? 'var(--bs-primary)' 
          : 'var(--bs-component-hover-bg)',
        color: state.isSelected 
          ? 'var(--bs-white)' 
          : 'var(--bs-body-color)',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'var(--bs-primary-bg-subtle)',
      borderRadius: '0.25rem',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'var(--bs-primary-text-emphasis)',
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'var(--bs-primary-text-emphasis)',
      '&:hover': {
        backgroundColor: 'var(--bs-primary)',
        color: 'var(--bs-white)',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--bs-secondary-color)',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: 'var(--bs-border-color)',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'var(--bs-secondary-color)',
      '&:hover': {
        color: 'var(--bs-body-color)',
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: 'var(--bs-secondary-color)',
      '&:hover': {
        color: 'var(--bs-danger)',
      },
    }),
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      const querySnapshot = await getDocs(collection(db, 'customers'));
      const customersData = querySnapshot.docs.map(doc => ({ 
        value: doc.id, 
        label: doc.data().company || doc.data().name 
      }));
      setCustomers(customersData);
    };
    fetchCustomers();
  }, []);

  /** Format a stored date value for the matching <input type>. */
  const toInputValue = (value: string | undefined, allDay: boolean, fallback: string): string => {
    if (!value) return fallback;
    return allDay ? moment(value).format('YYYY-MM-DD') : moment(value).format('YYYY-MM-DDTHH:mm');
  };

  useEffect(() => {
    if (event) {
      const allDay = event.allDay || false;
      // Prefill the day the user actually CLICKED, not the series' first day —
      // otherwise opening the 5th occurrence shows (and, on save, writes) the
      // original date. `occurrence` is null for a plain one-off event.
      const shownStart = occurrence?.start ?? event.start;
      const shownEnd = occurrence ? occurrence.end : event.end;
      setFormData({
        title: event.title,
        client: event.client || [],
        start: toInputValue(shownStart, allDay, moment().format('YYYY-MM-DDTHH:mm')),
        end: toInputValue(shownEnd, allDay, moment(shownStart).add(1, 'hours').format('YYYY-MM-DDTHH:mm')),
        allDay: allDay,
        description: event.description || '',
        location: event.location || '',
        attendees: event.attendees?.join(', ') || '',
        recurrence: event.recurrence?.join(', ') || '',
        recurrenceEnd: event.recurrenceEnd || '',
        colour: event.colour || '#3788d8',
      });
    } else {
      // Creating: anchor to the day the user clicked on, falling back to now.
      const base = defaultDate ? moment(defaultDate) : moment();
      setFormData({
        title: '',
        client: [],
        start: base.format('YYYY-MM-DDTHH:mm'),
        end: base.clone().add(1, 'hours').format('YYYY-MM-DDTHH:mm'),
        allDay: false,
        description: '',
        location: '',
        attendees: '',
        recurrence: '',
        recurrenceEnd: '',
        colour: '#3788d8',
      });
    }
    // Re-opening on a different occurrence must not inherit the last one's scope.
    setEditScope('occurrence');
  }, [event, defaultDate, occurrence]);

  // A stale error from a previous attempt must not greet the next open.
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Switching scope re-points the date fields at whichever record the save will
   * actually write — the occurrence's day, or the series' own start/end. The
   * user therefore always sees the dates they are about to commit, which is why
   * this needs no "re-anchor the edit back onto the series" date arithmetic.
   * Only the dates move; anything else already typed is preserved.
   */
  const handleScopeChange = (scope: EditScope) => {
    setEditScope(scope);
    if (!event || !occurrence) return;
    const src = scope === 'series'
      ? { start: event.start, end: event.end }
      : { start: occurrence.start, end: occurrence.end };
    setFormData(prev => ({
      ...prev,
      start: toInputValue(src.start, prev.allDay, prev.start),
      end: toInputValue(src.end, prev.allDay, prev.end),
    }));
  };

  const hasValidId = !!(event && typeof event.id === 'string' && event.id.trim().length > 0);
  // A series occurrence was opened AND the user left the scope on "this one only".
  const isSingleOccurrenceScope = hasValidId && !!occurrence && editScope === 'occurrence';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // "Ends on" is only meaningful for a recurrence, and an end before the start
    // yields a series with zero occurrences — which would look exactly like the
    // event silently vanishing.
    if (formData.recurrence && formData.recurrenceEnd) {
      const startDay = moment(formData.start).format('YYYY-MM-DD');
      if (formData.recurrenceEnd < startDay) {
        setError(`"Repeats until" (${formData.recurrenceEnd}) is before the event starts (${startDay}). The series would never occur.`);
        return;
      }
    }

    // firestore.rules requires `createdBy == request.auth.uid` on create, so a
    // missing uid is a guaranteed permission-denied. Fail with a sentence the
    // user can act on rather than a raw Firebase error.
    const uid = auth.currentUser?.uid ?? null;
    const willCreateDoc = !hasValidId || isSingleOccurrenceScope;
    if (willCreateDoc && !uid) {
      setError('You appear to be signed out, so the event cannot be saved. Please sign in again and retry — your changes are still here.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const nowIso = new Date().toISOString();
      const eventData: Record<string, any> = {
        title: formData.title,
        client: formData.client,
        start: formData.allDay ? moment(formData.start).format('YYYY-MM-DD') : moment(formData.start).toISOString(),
        end: formData.allDay ? moment(formData.end).format('YYYY-MM-DD') : moment(formData.end).toISOString(),
        allDay: formData.allDay,
        description: formData.description,
        location: formData.location,
        attendees: formData.attendees ? formData.attendees.split(',').map(a => a.trim()) : [],
        // A detached occurrence is a standalone one-off — it must NOT inherit the
        // series' recurrence or it would start repeating on its own.
        recurrence: isSingleOccurrenceScope ? [] : (formData.recurrence ? [formData.recurrence] : []),
        colour: formData.colour,
      };
      const wantsRecurrenceEnd = !isSingleOccurrenceScope && !!formData.recurrence && !!formData.recurrenceEnd;
      if (wantsRecurrenceEnd) {
        eventData.recurrenceEnd = formData.recurrenceEnd;
      }

      if (isSingleOccurrenceScope) {
        // Split this one day off the series: record it as an exception on the base
        // document and write the edited copy as its own event. Batched so we can
        // never end up having hidden the occurrence without replacing it (or the
        // reverse — a duplicate sitting on top of the original).
        const batch = writeBatch(db);
        batch.update(doc(collection(db, 'events'), event!.id), {
          exceptions: arrayUnion(occurrence!.date),
          updatedAt: nowIso,
        });
        batch.set(doc(collection(db, 'events')), {
          ...eventData,
          // Provenance, so a detached day can be traced back (and re-attached by
          // hand) instead of looking like an unrelated event someone typed in.
          detachedFrom: event!.id,
          detachedOccurrenceDate: occurrence!.date,
          createdBy: uid,
          createdAt: nowIso,
          updatedAt: nowIso,
          status: 'active',
        });
        await batch.commit();
      } else if (hasValidId) {
        // Clearing the recurrence (or its end date) must actually remove the
        // stored field — leaving a stale recurrenceEnd behind would silently cap
        // a series the user just made open-ended. `createdBy` is never touched.
        await updateDoc(doc(collection(db, 'events'), event!.id), {
          ...eventData,
          recurrenceEnd: wantsRecurrenceEnd ? formData.recurrenceEnd : deleteField(),
          updatedAt: nowIso,
        });
      } else {
        await addDoc(collection(db, 'events'), {
          ...eventData,
          createdBy: uid,
          createdAt: nowIso,
          updatedAt: nowIso,
          status: 'active',
        });
      }

      if (onNewEventAdded) {
        onNewEventAdded(eventData as CalendarEvent);
      }

      onClose();
    } catch (err) {
      console.error('Error saving event:', err);
      setError(
        `The event could not be saved${err instanceof Error && err.message ? `: ${err.message}` : '.'} Your changes are still here — please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    if (!hasValidId) return;
    try {
      setIsSubmitting(true);
      setError(null);
      const nowIso = new Date().toISOString();
      const eventRef = doc(collection(db, 'events'), event!.id);

      if (isSingleOccurrenceScope) {
        // Hide ONE day of the series. The base document keeps its dates and every
        // other occurrence, and the exception can be lifted by removing the date.
        await updateDoc(eventRef, {
          exceptions: arrayUnion(occurrence!.date),
          updatedAt: nowIso,
        });
      } else {
        // SOFT delete. The document survives (recoverable, and the audit trail
        // with it); readers hide it. NOTE for whoever touches the read side: the
        // test is `status !== 'cancelled'`, never `status === 'active'` — legacy
        // events have no status field at all.
        await updateDoc(eventRef, {
          status: 'cancelled',
          updatedAt: nowIso,
        });
      }

      if (onEventDeleted) {
        onEventDeleted(event!.id);
      }
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Error deleting event:', err);
      // Close the confirmation so the error is actually visible: the alert lives
      // in the edit modal underneath it.
      setShowDeleteConfirm(false);
      setError(
        `The event could not be deleted${err instanceof Error && err.message ? `: ${err.message}` : '.'} It is still in your calendar — please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isRecurringEvent = !!event?.recurrence && event.recurrence.length > 0;
  // The scope picker is only meaningful when the calendar told us WHICH day was
  // opened. Without that we cannot record an exception, so we must not offer a
  // choice we cannot honour.
  const canChooseScope = hasValidId && !!occurrence;

  return (
    <>
    {/* iOS auto-zooms the page whenever a focused input's text is under 16px, and
        inside a modal there is no way for the user to zoom back out. The date and
        time controls were rendering at 13.2px. 44px is the minimum comfortable
        touch target. Lives here rather than in Calendar.scss because this modal is
        also mounted from three non-calendar surfaces. */}
    <style>{`
      @media (max-width: 767.98px) {
        .event-modal-dateinput {
          font-size: 16px;
          min-height: 44px;
        }
      }
    `}</style>
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{event ? 'Edit Event' : 'Create New Event'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {canChooseScope && (
            <Alert variant="warning" className="py-3">
              <div className="fw-bold mb-2">This event repeats — what are you changing?</div>
              <Form.Check
                type="radio"
                id="event-scope-occurrence"
                name="event-scope"
                label={`Just this occurrence (${occurrence!.date})`}
                checked={editScope === 'occurrence'}
                onChange={() => handleScopeChange('occurrence')}
              />
              <Form.Check
                type="radio"
                id="event-scope-series"
                name="event-scope"
                label="The whole series — every occurrence, past and future"
                checked={editScope === 'series'}
                onChange={() => handleScopeChange('series')}
              />
              <div className="text-muted small mt-2">
                {editScope === 'occurrence'
                  ? 'This day will be split off into its own standalone event. The rest of the series is left exactly as it is.'
                  : 'The dates below now show the SERIES’ own start and end, not the day you clicked — saving rewrites every occurrence.'}
              </div>
            </Alert>
          )}

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Event Title *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter event title..."
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Associated Clients</Form.Label>
                <Select
                  isMulti
                  options={customers}
                  placeholder="Select clients..."
                  value={formData.client.map(client => customers.find(customer => customer.value === client)).filter((option): option is SelectOption => option !== undefined)}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    handleInputChange('client', values);
                  }}
                  styles={customSelectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Start {formData.allDay ? 'Date' : 'Date & Time'} *</Form.Label>
                <Form.Control
                  className="event-modal-dateinput"
                  type={formData.allDay ? "date" : "datetime-local"}
                  value={formData.start}
                  onChange={(e) => handleInputChange('start', e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>End {formData.allDay ? 'Date' : 'Date & Time'} *</Form.Label>
                <Form.Control
                  className="event-modal-dateinput"
                  type={formData.allDay ? "date" : "datetime-local"}
                  value={formData.end}
                  onChange={(e) => handleInputChange('end', e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="All Day Event"
                  checked={formData.allDay}
                  onChange={(e) => {
                    const allDay = e.target.checked;
                    handleInputChange('allDay', allDay);
                    if (allDay) {
                      handleInputChange('start', moment(formData.start).format('YYYY-MM-DD'));
                      handleInputChange('end', moment(formData.end).format('YYYY-MM-DD'));
                    } else {
                      handleInputChange('start', moment(formData.start).format('YYYY-MM-DDTHH:mm'));
                      handleInputChange('end', moment(formData.end).format('YYYY-MM-DDTHH:mm'));
                    }
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Event Color</Form.Label>
                <div className="d-flex gap-2 mt-1">
                  {colorOptions.map((color) => (
                    <div
                      key={color}
                      className={`border rounded-circle ${formData.colour === color ? 'border-primary border-3' : 'border-secondary'}`}
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: color,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleInputChange('colour', color)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Event description..."
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Location</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Event location..."
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Recurrence</Form.Label>
                <Form.Select
                  value={formData.recurrence}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('recurrence', value);
                    // Dropping the recurrence must drop its end date too, or a
                    // stale bound would be re-applied the next time one is set.
                    if (!value) handleInputChange('recurrenceEnd', '');
                  }}
                >
                  <option value="">No recurrence</option>
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="YEARLY">Yearly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* An open-ended series is the right default, but a job that runs for six
              weeks should stop after six weeks rather than repeating forever. */}
          {formData.recurrence && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Repeats until</Form.Label>
                  <Form.Control
                    className="event-modal-dateinput"
                    type="date"
                    value={formData.recurrenceEnd}
                    min={moment(formData.start).format('YYYY-MM-DD')}
                    onChange={(e) => handleInputChange('recurrenceEnd', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Inclusive — the event still occurs on this day. Leave empty to repeat indefinitely.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row>
            <Col>
              <Form.Group>
                <Form.Label>Attendees</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.attendees}
                  onChange={(e) => handleInputChange('attendees', e.target.value)}
                  placeholder="Enter attendees separated by commas..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {hasValidId && (
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSubmitting}
                >
                  {isSingleOccurrenceScope ? 'Delete This Occurrence' : 'Delete Event'}
                </Button>
              )}
            </div>
            <div>
              <Button 
                variant="secondary" 
                onClick={onClose}
                disabled={isSubmitting}
                className="me-2"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? 'Saving...'
                  : !event
                    ? 'Create Event'
                    : isSingleOccurrenceScope
                      ? 'Save This Occurrence'
                      : canChooseScope
                        ? 'Update Whole Series'
                        : 'Update Event'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>

    {/* Metronic-styled Delete Confirmation */}
    <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center">
          <KTSVG path="/media/icons/duotune/general/gen044.svg" className="svg-icon-2hx text-danger me-4" />
          <div>
            <div className="fw-bold">
              {isSingleOccurrenceScope
                ? `Remove the ${occurrence!.date} occurrence?`
                : isRecurringEvent
                  ? 'Delete the entire recurring series?'
                  : 'Delete this event?'}
            </div>
            <div className="text-muted">
              {isSingleOccurrenceScope
                ? 'Only this one day is removed from the series. Every other occurrence stays exactly where it is, and this day can be restored later.'
                : isRecurringEvent
                  ? 'This event repeats. Deleting it removes the whole series — every occurrence, past and future — not just the one you opened. The event is kept as cancelled rather than erased, so it can be restored.'
                  : 'The event is kept as cancelled rather than erased, so it can be restored if this was a mistake.'}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={isSubmitting}>
          {isSubmitting ? 'Deleting…' : 'Delete'}
        </button>
      </Modal.Footer>
    </Modal>
  </>
  );
};

export default NewEventModal;